import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { transferInquiryService, transferResponseService, subscribeToInquiries } from '../../services/transferMarketplaceService';
import { playerProfileService } from '../../services/playerMasterService';
import InquiryCard from './components/InquiryCard';
import CreateInquiryModal from './components/CreateInquiryModal';
import RespondToInquiryModal from './components/RespondToInquiryModal';
import { supabase } from '../../lib/supabase';

const LiveTransferMarketplace = () => {
  const { user, userProfile } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    loadInquiries();
    loadPlayers();
    
    const channel = subscribeToInquiries(() => {
      loadInquiries();
    });

    return () => {
      if (channel) supabase?.removeChannel(channel);
    };
  }, []);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const { data } = await transferInquiryService?.getAll();
      setInquiries(data || []);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlayers = async () => {
    try {
      const { data } = await playerProfileService?.getAll();
      setPlayers(data || []);
    } catch (error) {
      console.error('Error loading players:', error);
    }
  };

  const handleCreateInquiry = async (formData) => {
    try {
      setSubmitLoading(true);
      await transferInquiryService?.create({
        ...formData,
        clubId: user?.id
      });
      setShowCreateModal(false);
      loadInquiries();
    } catch (error) {
      console.error('Error creating inquiry:', error);
      alert(error?.message || 'Failed to create inquiry');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRespondToInquiry = async (formData) => {
    try {
      setSubmitLoading(true);
      await transferResponseService?.create({
        ...formData,
        respondentId: user?.id
      });
      setShowRespondModal(false);
      setSelectedInquiry(null);
      loadInquiries();
    } catch (error) {
      console.error('Error submitting response:', error);
      alert(error?.message || 'Failed to submit response');
    } finally {
      setSubmitLoading(false);
    }
  };

  const filteredInquiries = inquiries?.filter(inquiry => {
    const matchesFilter = filter === 'all' || inquiry?.status === filter;
    const matchesSearch = !searchTerm || 
      inquiry?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      inquiry?.positionRequired?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = [
    {
      label: 'Active Inquiries',
      value: inquiries?.filter(i => i?.status === 'open')?.length || 0,
      icon: 'FileText',
      color: 'primary'
    },
    {
      label: 'In Negotiation',
      value: inquiries?.filter(i => i?.status === 'in_negotiation')?.length || 0,
      icon: 'MessageSquare',
      color: 'accent'
    },
    {
      label: 'Total Responses',
      value: inquiries?.reduce((sum, i) => sum + (i?.responsesCount || 0), 0),
      icon: 'Send',
      color: 'success'
    },
    {
      label: 'Available Players',
      value: players?.filter(p => p?.availableForTransfer)?.length || 0,
      icon: 'Users',
      color: 'secondary'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Live Transfer Marketplace - ProX11Market</title>
        <meta name="description" content="Interactive real-time marketplace for transfer negotiations between clubs and player representatives" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Live Transfer Marketplace</h1>
                  <p className="text-muted-foreground">Real-time transfer negotiations and player opportunities</p>
                </div>
                {userProfile?.role === 'club' && (
                  <Button
                    variant="default"
                    onClick={() => setShowCreateModal(true)}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Post Inquiry
                  </Button>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                {stats?.map((stat, index) => (
                  <div key={index} className="bg-card border border-border rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 bg-${stat?.color}/10 rounded-lg flex items-center justify-center`}>
                        <Icon name={stat?.icon} size={24} color={`var(--color-${stat?.color})`} />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-foreground">{stat?.value}</div>
                        <div className="text-xs text-muted-foreground">{stat?.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search inquiries by title or position..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {['all', 'open', 'in_negotiation', 'closed']?.map(status => (
                    <button
                      key={status}
                      onClick={() => setFilter(status)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        filter === status
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {status === 'all' ? 'All' : status?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Inquiries List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading inquiries...</p>
                </div>
              </div>
            ) : filteredInquiries?.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No inquiries found</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your filters or search term</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredInquiries?.map(inquiry => (
                  <InquiryCard
                    key={inquiry?.id}
                    inquiry={inquiry}
                    onViewDetails={(inq) => setSelectedInquiry(inq)}
                    onRespond={(inq) => {
                      setSelectedInquiry(inq);
                      setShowRespondModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateInquiryModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateInquiry}
          loading={submitLoading}
        />
      )}

      {showRespondModal && selectedInquiry && (
        <RespondToInquiryModal
          inquiry={selectedInquiry}
          players={players}
          onClose={() => {
            setShowRespondModal(false);
            setSelectedInquiry(null);
          }}
          onSubmit={handleRespondToInquiry}
          loading={submitLoading}
        />
      )}
    </>
  );
};

export default LiveTransferMarketplace;